/**
 * Quick View Modal with FLIP Animation
 * Matches Figma gallery interaction style
 */

(function() {
  'use strict';
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const ANIMATION_DURATION = prefersReducedMotion ? 0 : 550;
  const EASING = 'cubic-bezier(0.22, 0.61, 0.36, 1)';

  /**
   * Quick View MODAL (product-card click -> modal overlay) is intentionally disabled.
   * We keep the code in place so it can be re-enabled later by flipping this flag.
   *
   * Note: This file also powers the custom PDP (`sections/product-main.liquid`) for:
   * - variant selection UI
   * - price/inventory updates
   * - add-to-cart submission
   * Those PDP behaviors remain enabled.
   */
  const ENABLE_QUICKVIEW_MODAL = false;


  function parseVariants(root) {
    const el = root.querySelector('[data-variants-json]');
    if (!el) return [];
    try { return JSON.parse(el.textContent || '[]'); } catch { return []; }
  }
  
  function isFiniteNumber(n) {
    return typeof n === 'number' && Number.isFinite(n);
  }

  // Our definition of "in stock" for the UI:
  // - If Shopify is tracking inventory and we have a quantity, treat qty > 0 as in-stock.
  // - Otherwise fall back to Shopify's `available` flag.
  function isInStock(variant) {
    if (!variant) return false;
    const managedByShopify = variant.inventory_management === 'shopify';
    const qty = Number(variant.inventory_quantity);
    if (managedByShopify && isFiniteNumber(qty)) {
      return qty > 0;
    }
    return variant.available === true;
  }

  function normalizeOpt(val) {
    if (val == null) return null;
    // Normalize for reliable matching between DOM dataset values and variant JSON options
    // - trim
    // - collapse whitespace
    // - case-insensitive compare
    return String(val)
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  }

  function getSelectedOptions(root) {
    const groups = [...root.querySelectorAll('.dg-quickview__variant-options[data-option-index]')];
    const selections = [];
    for (const g of groups) {
      const idx = Number(g.dataset.optionIndex);
      const selectedBtn = g.querySelector('.dg-quickview__variant-option.is-selected');
      selections[idx] = selectedBtn ? normalizeOpt(selectedBtn.dataset.value) : null;
    }
    return selections;
  }
  
  function findMatchingVariant(variants, selections) {
    return variants.find(v => {
      if (!v || !Array.isArray(v.options)) return false;
      for (let i = 0; i < selections.length; i++) {
        if (selections[i] == null) continue;
        if (normalizeOpt(v.options[i]) !== normalizeOpt(selections[i])) return false;
      }
      return true;
    }) || null;
  }
  
  function setInventoryUI(root, variant) {
    const txt = root.querySelector('[data-inventory-text]');
    if (!txt) return;
  
    if (!variant) {
      txt.textContent = '';
      return;
    }
  
    const managedByShopify = variant.inventory_management === 'shopify';
    const qty = Number(variant.inventory_quantity);

    if (!isInStock(variant)) {
      txt.textContent = 'Sold out';
      return;
    }

    // If we have a real quantity number, show it; otherwise just show "In stock"
    if (managedByShopify && isFiniteNumber(qty)) {
      txt.textContent = `${qty} in stock`;
      return;
    }

    txt.textContent = 'In stock';
  }
  
  function setHiddenVariantId(root, variant) {
    const input = root.querySelector('input[data-variant-id]');
    if (input && variant && variant.id) input.value = variant.id;
  }
  
  function updateOptionAvailability(root, variants) {
    const selections = getSelectedOptions(root);
  
    const groups = [...root.querySelectorAll('.dg-quickview__variant-options[data-option-index]')];
    for (const group of groups) {
      const groupIdx = Number(group.dataset.optionIndex);
      const buttons = [...group.querySelectorAll('.dg-quickview__variant-option[data-value]')];
  
      for (const btn of buttons) {
        const val = normalizeOpt(btn.dataset.value);
        const test = selections.slice();
        test[groupIdx] = val;
  
        const match = variants.find(v => {
          if (!v || !Array.isArray(v.options)) return false;
          for (let i = 0; i < test.length; i++) {
            if (test[i] == null) continue;
            if (normalizeOpt(v.options[i]) !== normalizeOpt(test[i])) return false;
          }
          return isInStock(v);
        });
  
        const disabled = !match;
        btn.disabled = disabled;
        btn.classList.toggle('is-disabled', disabled);
      }
    }
  }
  
  function setAddToCartState(root, variant) {
    const btn = root.querySelector('[data-add-to-cart]');
    const loadingEl = btn ? btn.querySelector('[data-loading]') : null;
    const buttonText = btn ? btn.querySelector('[data-button-text]') : null;
    if (!btn) return;

    const inStock = isInStock(variant);
    btn.disabled = !inStock;
    btn.classList.toggle('is-disabled', !inStock);
    btn.setAttribute('aria-disabled', inStock ? 'false' : 'true');

    if (loadingEl) loadingEl.style.display = 'none';
    if (buttonText) {
      if (!variant) {
        buttonText.textContent = 'Unavailable';
      } else if (!inStock) {
        buttonText.textContent = 'Sold out';
      } else {
        buttonText.textContent = buttonText.textContent || 'Add to cart';
      }
      buttonText.style.display = 'inline';
    }
  }

  function updateQuickviewVariantState(root) {
    const variants = parseVariants(root);
    if (!variants.length) return;
  
    const selections = getSelectedOptions(root);
    const variant = findMatchingVariant(variants, selections);
  
    setHiddenVariantId(root, variant);
    setInventoryUI(root, variant);
    if (variant) updatePrice(root, variant);
    setAddToCartState(root, variant);
    updateOptionAvailability(root, variants);
    updateQuantityControls(root, variant);
  }

  // --- Quantity stepper (PDP) ---
  async function fetchCart() {
    const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
    if (!res.ok) throw new Error('Failed to fetch cart');
    return await res.json();
  }

  function findLineItemForVariant(cart, variantId) {
    const id = Number(variantId);
    if (!cart || !Array.isArray(cart.items) || !Number.isFinite(id)) return null;
    return cart.items.find(it => Number(it.variant_id) === id) || null;
  }

  function setQtyUI(root, qty, { disableAll = false } = {}) {
    const wrap = root.querySelector('[data-qty-root]');
    if (!wrap) return;
    const addBtn = wrap.querySelector('[data-qty-add]');
    const addBtnText = addBtn ? addBtn.querySelector('[data-qty-count]') : null;
    const countDisplay = wrap.querySelector('[data-qty-count-display]');
    const decBtn = wrap.querySelector('[data-qty-dec]');
    const incBtn = wrap.querySelector('[data-qty-inc]');

    const safeQty = Number.isFinite(Number(qty)) ? Math.max(0, Number(qty)) : 0;
    const hasItems = safeQty > 0;
    
    wrap.classList.toggle('has-items', hasItems);
    
    if (addBtnText) {
      addBtnText.textContent = 'Add to cart';
    }
    if (countDisplay) {
      countDisplay.textContent = `${safeQty} in cart`;
    }
    if (addBtn) {
      addBtn.disabled = disableAll;
    }

    if (decBtn) decBtn.disabled = disableAll || safeQty <= 0;
    if (incBtn) incBtn.disabled = disableAll;
  }

  async function refreshQtyUI(root, variantId) {
    try {
      if (!variantId || !Number.isFinite(Number(variantId))) {
        setQtyUI(root, 0, { disableAll: true });
        return;
      }
      const cart = await fetchCart();
      const item = findLineItemForVariant(cart, variantId);
      setQtyUI(root, item ? item.quantity : 0, { disableAll: false });
    } catch {
      // If cart fetch fails, keep controls usable but show 0.
      setQtyUI(root, 0, { disableAll: false });
    }
  }

  function updateQuantityControls(root, variant) {
    const wrap = root.querySelector('[data-qty-root]');
    if (!wrap) return; // only on PDP
    const variantId = variant && variant.id ? Number(variant.id) : null;
    const inStock = isInStock(variant);

    // Disable inc when out of stock / no variant selected
    const incBtn = wrap.querySelector('[data-qty-inc]');
    if (incBtn) incBtn.disabled = !variantId || !inStock;

    // Refresh qty count from cart (async)
    refreshQtyUI(root, variantId);
  }

  // Create modal container if it doesn't exist
  function ensureModalContainer() {
    let modal = document.getElementById('dg-quickview');
    if (!modal) {
      modal = document.createElement('div');
      modal.id = 'dg-quickview';
      modal.className = 'dg-qv is-hidden';
      modal.setAttribute('role', 'dialog');
      modal.setAttribute('aria-modal', 'true');
      modal.setAttribute('aria-labelledby', 'dg-quickview-title');
      document.body.appendChild(modal);
    }
    return modal;
  }

  const modal = ENABLE_QUICKVIEW_MODAL ? ensureModalContainer() : null;
  let currentCard = null;
  let currentImageClone = null;
  let isAnimating = false;
  let isOpen = false;
  let currentProductHandle = null;
  let selectedVariantId = null;
  let productVariants = null;

  // Lock/unlock body scroll
  function lockBodyScroll() {
    // BY ME, DONT HIDE SCROLL BAR, BUT CONTENT WILL REMAIN CENTERED HOPEFULLY
    document.body.style.overflow = '';
  }

  function unlockBodyScroll() {
    document.body.style.overflow = '';
  }

  // Focus trap
  function trapFocus(element) {
    const focusableElements = element.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    function handleTab(e) {
      if (e.key !== 'Tab') return;
      
      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    }

    element.addEventListener('keydown', handleTab);
    return () => element.removeEventListener('keydown', handleTab);
  }

  // Get image element from card
  function getCardImage(card) {
    return card.querySelector('.dg-card__img, .artwork-tile__image:not(.artwork-tile__image--secondary)');
  }

  // Create transition clone
  function createImageClone(img, rect) {
    const clone = img.cloneNode(true);
    clone.style.cssText = `
      position: fixed;
      top: ${rect.top}px;
      left: ${rect.left}px;
      width: ${rect.width}px;
      height: ${rect.height}px;
      object-fit: cover;
      z-index: 10000;
      pointer-events: none;
      transition: transform ${ANIMATION_DURATION}ms ${EASING};
    `;
    document.body.appendChild(clone);
    return clone;
  }

  // FLIP animation: First (measure), Last (measure), Invert, Play
  function animateImageFlip(sourceRect, targetRect, clone) {
    if (prefersReducedMotion || !clone) {
      if (clone) clone.remove();
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      // Calculate transform
      const scaleX = targetRect.width / sourceRect.width;
      const scaleY = targetRect.height / sourceRect.height;
      const translateX = targetRect.left - sourceRect.left;
      const translateY = targetRect.top - sourceRect.top;

      // Apply transform
      clone.style.transform = `translate(${translateX}px, ${translateY}px) scale(${scaleX}, ${scaleY})`;

      clone.addEventListener('transitionend', function onEnd() {
        clone.removeEventListener('transitionend', onEnd);
        clone.remove();
        resolve();
      }, { once: true });

      // Fallback timeout
      setTimeout(() => {
        clone.remove();
        resolve();
      }, ANIMATION_DURATION + 100);
    });
  }

  // Fetch product content
  async function fetchProductContent(productUrl) {
    // Shopify section rendering format
    const url = new URL(productUrl, window.location.origin);
    url.searchParams.set('section_id', 'dg-quickview-product');
    
    try {
      const response = await fetch(url.toString());
      if (!response.ok) throw new Error('Failed to fetch product');
      const html = await response.text();
      
      // Shopify returns HTML wrapped in a section tag, extract content
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html.trim();
      
      // Find the section content
      const section = tempDiv.querySelector('section') || tempDiv.querySelector('.dg-quickview') || tempDiv;
      return section;
    } catch (error) {
      console.error('Quick View fetch error:', error);
      throw error;
    }
  }

  // Update cart count bubble (ONLY update count, never replace icon)
  function updateCartCount() {
    fetch('/cart.js')
      .then(res => res.json())
      .then(cart => {
        const cartIcon = document.getElementById('cart-icon-bubble');
        if (!cartIcon) return;

        const bubble = cartIcon.querySelector('.cart-count-bubble');
        const count = cart.item_count || 0;

        if (count > 0) {
          if (!bubble) {
            // Create ONLY the bubble, don't touch the icon
            const newBubble = document.createElement('div');
            newBubble.className = 'cart-count-bubble';
            const countSpan = document.createElement('span');
            countSpan.setAttribute('aria-hidden', 'true');
            countSpan.textContent = count;
            const hiddenSpan = document.createElement('span');
            hiddenSpan.className = 'visually-hidden';
            hiddenSpan.textContent = `${count} items in cart`;
            newBubble.appendChild(countSpan);
            newBubble.appendChild(hiddenSpan);
            cartIcon.appendChild(newBubble);
          } else {
            // Update ONLY the count text
            const countSpan = bubble.querySelector('span[aria-hidden="true"]');
            const hiddenSpan = bubble.querySelector('.visually-hidden');
            if (countSpan) countSpan.textContent = count;
            if (hiddenSpan) hiddenSpan.textContent = `${count} items in cart`;
          }
        } else {
          // Remove bubble if cart is empty
          if (bubble) bubble.remove();
        }
      })
      .catch(err => console.error('Cart count update error:', err));
  }

  // Initialize variant selection (selection happens here; click handling is done by the unified click listener)
  function initVariants(quickviewEl) {
    const variantsJson = quickviewEl.querySelector('[data-variants-json]');
    if (!variantsJson) {
      // Single variant product
      const variantInput = quickviewEl.querySelector('[data-variant-id]');
      if (variantInput) {
        selectedVariantId = variantInput.value;
      }
      return;
    }

    productVariants = JSON.parse(variantsJson.textContent);
    const optionGroups = quickviewEl.querySelectorAll('[data-option-index]');

    // 1) Pick an initial in-stock variant (fall back to first)
    const firstAvailableVariant = productVariants.find(v => isInStock(v)) || productVariants[0];

    // 2) Mark its option buttons as selected (if we can find matching buttons)
    if (firstAvailableVariant && Array.isArray(firstAvailableVariant.options)) {
      firstAvailableVariant.options.forEach((opt, idx) => {
        const group = Array.from(optionGroups).find(g => Number(g.dataset.optionIndex) === idx);
        if (!group) return;
        const button = Array.from(group.querySelectorAll('.dg-quickview__variant-option')).find(
          btn => normalizeOpt(btn.dataset.value) === normalizeOpt(opt)
        );
        if (button && !button.disabled && !button.classList.contains('is-disabled')) {
          group.querySelectorAll('.dg-quickview__variant-option.is-selected').forEach(b => b.classList.remove('is-selected'));
          button.classList.add('is-selected');
        }
      });
    }

    // 3) Finalize initial state (inventory, availability, add-to-cart)
    updateQuickviewVariantState(quickviewEl);
  }

  // Update price display
  function updatePrice(quickviewEl, variant) {
    const priceEl = quickviewEl.querySelector('[data-price]');
    if (!priceEl || !variant) return;

    const price = variant.price;
    const comparePrice = variant.compare_at_price;
    
    let priceHtml = '';
    if (comparePrice && comparePrice > price) {
      priceHtml = `
        <span class="visually-hidden">Regular price</span>
        <s class="price-item price-item--regular">${formatMoney(comparePrice)}</s>
        <span class="visually-hidden">Sale price</span>
        <span class="price-item price-item--sale">${formatMoney(price)}</span>
      `;
    } else {
      priceHtml = `
        <span class="price-item">${formatMoney(price)}</span>
      `;
    }
    
    priceEl.innerHTML = priceHtml;
  }

  // Format money (simplified - use Shopify's money format if available)
  function formatMoney(cents) {
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  // Handle add to cart (real)
  let addToCartTimeout = null;
  
  function handleAddToCart(quickviewEl, e) {
    e.preventDefault();
    
    // Clear any existing timeout
    if (addToCartTimeout) {
      clearTimeout(addToCartTimeout);
      addToCartTimeout = null;
    }

    const addButton = quickviewEl.querySelector('[data-add-to-cart]');
    if (!addButton) return;
    const loadingEl = quickviewEl.querySelector('[data-loading]');
    const buttonText = addButton.querySelector('[data-button-text]');
    const variantInput = quickviewEl.querySelector('input[data-variant-id]');
    const variantId = variantInput ? Number(variantInput.value) : null;

    if (!variantId || !Number.isFinite(variantId)) {
      showError(quickviewEl, 'Please select a variant');
      return;
    }
    
    // Store original text if not already stored
    if (!addButton.dataset.originalText) {
      addButton.dataset.originalText = buttonText ? buttonText.textContent : 'Add to cart';
    }
    const originalText = addButton.dataset.originalText;

    // Show loading state
    addButton.disabled = true;
    if (buttonText) {
      buttonText.style.display = 'none';
    }
    if (loadingEl) {
      loadingEl.style.display = 'inline';
    }

    // Add to cart
    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        id: variantId,
        quantity: 1
      })
    })
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok || data?.status === 422 || data?.error) {
          const msg = data?.description || data?.message || 'Failed to add to cart';
          throw new Error(msg);
        }
        return data;
      })
      .then(() => {
        // Update cart count bubble
        updateCartCount();

        // Show "Added!" confirmation
        if (buttonText) {
          buttonText.textContent = 'Added!';
          buttonText.style.display = 'inline';
        }
        if (loadingEl) {
          loadingEl.style.display = 'none';
        }

        // Reset to original text after 900ms
        addToCartTimeout = setTimeout(() => {
          if (buttonText) {
            buttonText.textContent = originalText;
          }
          // Recompute button enabled state based on current selection
          updateQuickviewVariantState(quickviewEl);
          addToCartTimeout = null;
        }, 900);
      })
      .catch((err) => {
        showError(quickviewEl, err?.message || 'Failed to add to cart');
        if (loadingEl) loadingEl.style.display = 'none';
        if (buttonText) {
          buttonText.textContent = originalText;
          buttonText.style.display = 'inline';
        }
        // Recompute button enabled state based on current selection
        updateQuickviewVariantState(quickviewEl);
        addToCartTimeout = null;
      });
  }
  
  // Clear timeout when opening new quickview
  function clearAddToCartState() {
    if (addToCartTimeout) {
      clearTimeout(addToCartTimeout);
      addToCartTimeout = null;
    }
  }

  // Allow the same Quickview-style PDP markup to work on product pages too.
  // We delegate submit handling so it works for both:
  // - quickview modal-injected forms
  // - server-rendered PDP forms using the same markup
  document.addEventListener('submit', (e) => {
    const form = e.target && e.target.closest && e.target.closest('[data-quickview-form]');
    if (!form) return;
    const root = form.closest('[data-dg-variant-root]') || form.closest('.dg-quickview');
    if (!root) return;
    handleAddToCart(root, e);
  });

  // Show error message
  function showError(quickviewEl, message) {
    const errorEl = quickviewEl.querySelector('[data-error]');
    if (errorEl) {
      errorEl.textContent = message;
      errorEl.hidden = false;
      setTimeout(() => {
        errorEl.hidden = true;
      }, 5000);
    }
  }
  // Carousel behavior is shared between Quickview and PDP via `assets/dg-carousel.js`
  // (translate3d track, wrap-around arrows, pointer swipe).

  // Unified click event listener for all interactions
  document.addEventListener('click', function(e) {
    // 1. Quickview modal trigger (DISABLED)
    if (ENABLE_QUICKVIEW_MODAL) {
      const card = e.target.closest('[data-quickview-trigger]');
      if (card) {
        e.preventDefault();
        e.stopPropagation();
        openModal(card);
        return;
      }
    }

    // 2. Check if clicked outside modal (backdrop)
    if (ENABLE_QUICKVIEW_MODAL && modal && isOpen && e.target === modal) {
      closeModal();
      return;
    }

    // 3. Check if clicked close button
    const closeBtn = e.target.closest('.dg-qv__close, [data-close-quickview]');
    if (ENABLE_QUICKVIEW_MODAL && modal && closeBtn) {
      e.preventDefault();
      closeModal();
      return;
    }

    // 4. Check if clicked variant option button
    const variantBtn = e.target.closest('.dg-quickview__variant-option');
    if (variantBtn && !variantBtn.disabled) {
      const root = variantBtn.closest('[data-dg-variant-root]') || variantBtn.closest('.dg-quickview');
      if (root) {
        const group = variantBtn.closest('.dg-quickview__variant-options');
        if (group) {
          group.querySelectorAll('.dg-quickview__variant-option.is-selected')
            .forEach(b => b.classList.remove('is-selected'));
        }
        variantBtn.classList.add('is-selected');
        updateQuickviewVariantState(root);
      }
      return;
    }

    // 5. Qty stepper (PDP) - per selected variant/size
    const addBtn = e.target.closest('[data-qty-add]');
    const inc = e.target.closest('[data-qty-inc]');
    const dec = e.target.closest('[data-qty-dec]');
    if (addBtn || inc || dec) {
      e.preventDefault();
      const root = (addBtn || inc || dec).closest('[data-dg-variant-root]') || (addBtn || inc || dec).closest('.dg-quickview');
      if (!root) return;

      const variantInput = root.querySelector('input[data-variant-id]');
      const variantId = variantInput ? Number(variantInput.value) : null;
      if (!variantId || !Number.isFinite(variantId)) return;

      const wrap = root.querySelector('[data-qty-root]');
      if (!wrap) return;
      const addButton = wrap.querySelector('[data-qty-add]');
      const incBtn = wrap.querySelector('[data-qty-inc]');
      const decBtn = wrap.querySelector('[data-qty-dec]');
      if (addButton) addButton.disabled = true;
      if (incBtn) incBtn.disabled = true;
      if (decBtn) decBtn.disabled = true;

      (async () => {
        try {
          if (addBtn || inc) {
            // Add to cart
            const res = await fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
              body: JSON.stringify({ id: variantId, quantity: 1 })
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok || data?.status === 422 || data?.error) {
              throw new Error(data?.description || data?.message || 'Failed to add to cart');
            }
          } else {
            // Decrease quantity
            const cart = await fetchCart();
            const item = findLineItemForVariant(cart, variantId);
            const currentQty = item ? Number(item.quantity) : 0;
            if (currentQty > 0 && item?.key) {
              const nextQty = currentQty - 1;
              const res = await fetch('/cart/change.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                body: JSON.stringify({ id: item.key, quantity: nextQty })
              });
              const data = await res.json().catch(() => ({}));
              if (!res.ok || data?.status === 422 || data?.error) {
                throw new Error(data?.description || data?.message || 'Failed to update cart');
              }
            }
          }

          updateCartCount();
          // Recompute UI state (stock + qty)
          updateQuickviewVariantState(root);
        } catch (err) {
          showError(root, err?.message || 'Cart update failed');
          updateQuickviewVariantState(root);
        }
      })();
      return;
    }
  });

  // Prevent navigation on card links (capture phase)
  document.addEventListener('click', function(e) {
    if (!ENABLE_QUICKVIEW_MODAL) return;
    const link = e.target.closest('.dg-card__link');
    if (link && link.closest('[data-quickview-trigger]')) {
      e.preventDefault();
    }
  }, true);

  // Open modal
  async function openModal(card) {
    if (!ENABLE_QUICKVIEW_MODAL || !modal) return;
    if (isAnimating || isOpen) return;
    
    const handle = card.dataset.handle;
    const productUrl = card.dataset.productUrl || `/products/${handle}`;
    
    if (currentProductHandle === handle && isOpen) {
      // Already showing this product
      return;
    }

    isAnimating = true;
    currentCard = card;
    currentProductHandle = handle;

    // Show modal INSTANTLY - no delays, no transitions
    // Remove hidden class and add open class synchronously
    modal.classList.remove('is-hidden');
    modal.classList.add('is-open');
    
    // Set innerHTML and lock scroll immediately
    modal.innerHTML = '<div class="dg-qv__loading">Loading...</div>';
    lockBodyScroll();
    
    // Force synchronous layout to ensure modal is visible immediately
    modal.offsetHeight; // Force reflow
    
    // Set state immediately so modal is visible
    isOpen = true;
    isAnimating = false;

    try {
      // Fetch content in background (modal already visible)
      const content = await fetchProductContent(productUrl);
      
      // Clear loading, insert content
      modal.innerHTML = '';
      modal.appendChild(content);
      
      // Initialize
      const quickviewEl = modal.querySelector('.dg-quickview');
      if (quickviewEl) {
        // Clear any previous add-to-cart state
        clearAddToCartState();
        
        initVariants(quickviewEl);
        if (window.DGCarousel && typeof window.DGCarousel.mountAll === 'function') {
          window.DGCarousel.mountAll(quickviewEl);
        }
        
        // Update variant state after HTML is injected
        updateQuickviewVariantState(quickviewEl);
        
        // Clear any leftover "Adding..." UI (but do NOT override availability state)
        const addButton = quickviewEl.querySelector('[data-add-to-cart]');
        if (addButton) {
          const loadingEl = addButton.querySelector('[data-loading]');
          if (loadingEl) loadingEl.style.display = 'none';
          delete addButton.dataset.originalText;
        }
      }

      // Focus trap
      trapFocus(modal);

      // Focus first element
      const firstFocusable = modal.querySelector('button, [href], input');
      if (firstFocusable) firstFocusable.focus();

    } catch (error) {
      console.error('Error opening quick view:', error);
      modal.innerHTML = '<div class="dg-qv__error">Failed to load product. <button class="dg-qv__close">Close</button></div>';
      isAnimating = false;
    }
  }

  // Close modal
  function closeModal() {
    if (!ENABLE_QUICKVIEW_MODAL || !modal) return;
    if (!isOpen || isAnimating) return;
    
    isAnimating = true;
    modal.classList.remove('is-open');
    modal.classList.add('is-hidden');
    modal.innerHTML = '';
    unlockBodyScroll();
    isOpen = false;
    isAnimating = false;
    currentCard = null;
    currentProductHandle = null;
  }

  // ESC key handler
  document.addEventListener('keydown', function(e) {
    if (!ENABLE_QUICKVIEW_MODAL) return;
    if (e.key === 'Escape' && isOpen) {
      closeModal();
    }
  });

  // Initialize PDP instances that reuse the Quickview interaction patterns (variants, price/inventory, add-to-cart),
  // but are NOT the modal.
  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('[data-dg-pdp-root]').forEach((root) => {
      initVariants(root);
      updateQuickviewVariantState(root);
      if (window.DGCarousel && typeof window.DGCarousel.mountAll === 'function') {
        window.DGCarousel.mountAll(root);
      }
    });
  });
})();

